import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  useDisclosure,
} from '@elkaso-app/web-design';
import { useCreateFacilityRepaymentApi } from 'apis/bnpl';
import { useGetCreditFacilityApi } from 'apis/bnpl';
import { formatAmount } from 'pages/bnpl/utils/bnpl-statuses';
import React from 'react';
import { useForm } from 'react-hook-form';

interface RecordRepaymentModalProps {
  trigger: JSX.Element;
  facilityId: number;
  utilizedAmount: number;
}

interface FormInputs {
  amount: string;
  notes: string;
}

export const RecordRepaymentModal = ({ trigger, facilityId, utilizedAmount }: RecordRepaymentModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { mutate, isLoading } = useCreateFacilityRepaymentApi();
  const { refetch } = useGetCreditFacilityApi(facilityId, { enabled: false });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onChange',
    defaultValues: { amount: '', notes: '' },
  });

  const onCloseModal = () => {
    reset({ amount: '', notes: '' });
    onClose();
  };

  const onSubmit = (data: FormInputs) => {
    mutate(
      {
        facilityId,
        body: {
          amount: parseFloat(data.amount),
          notes: data.notes || undefined,
        },
      },
      {
        onSuccess: () => {
          refetch();
          onCloseModal();
        },
      }
    );
  };

  return (
    <>
      {React.cloneElement(trigger, { onClick: onOpen })}

      <Modal isOpen={isOpen} onClose={onCloseModal} closeOnOverlayClick={false} size='md'>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Record Repayment</ModalHeader>
          <ModalBody>
            <Stack as='form' id='recordRepaymentForm' spacing='lg' onSubmit={handleSubmit(onSubmit)} noValidate>
              <Text fontSize='sm' color='gray.500'>
                Currently utilized: {formatAmount(utilizedAmount)}
              </Text>

              <FormControl isInvalid={!!errors.amount} isRequired>
                <FormLabel>Amount (AED)</FormLabel>
                <Input
                  type='number'
                  step='0.01'
                  min={0.01}
                  placeholder='Enter repayment amount'
                  {...register('amount', {
                    required: 'Amount is required',
                    min: { value: 0.01, message: 'Amount must be greater than 0' },
                    max: { value: utilizedAmount, message: `Amount cannot exceed ${formatAmount(utilizedAmount)}` },
                    validate: (value) => !isNaN(parseFloat(value)) || 'Please enter a valid number',
                  })}
                />
                <FormErrorMessage>{errors.amount?.message}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <FormLabel>Notes (optional)</FormLabel>
                <Textarea {...register('notes')} placeholder='e.g., Qlub confirmed March collection' />
              </FormControl>
            </Stack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing='md'>
              <Button variant='outline' colorScheme='red' onClick={onCloseModal}>
                Cancel
              </Button>
              <Button variant='solid' colorScheme='green' type='submit' form='recordRepaymentForm' isLoading={isLoading}>
                Record Repayment
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
